import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Flame, Waves, PawPrint, Star } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLine, Eyebrow } from "@/components/Reveal";
import { IMG, STATS, RETREATS } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative flex min-h-screen items-end overflow-hidden bg-[#1E1610]">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src={IMG.exterior} alt="Plas Llechylched courtyard estate at golden hour" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#1E1610] via-[#1E1610]/50 to-[#1E1610]/20" />
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#1E1610]/70 to-transparent" />
      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-40 sm:px-8 lg:px-16">
        <Eyebrow light className="mb-6">Plas Llechylched · Rhosneigr · Anglesey</Eyebrow>
        <h1 className="max-w-4xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-[#F4F1EA] sm:text-5xl lg:text-7xl">
          <MaskedLine delay={0.1}>A historic courtyard estate,</MaskedLine>
          <MaskedLine delay={0.25}>
            <em className="text-[#C89D56]">reimagined</em> for slow
          </MaskedLine>
          <MaskedLine delay={0.4}>days, dogs and sunsets.</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-xl text-base leading-relaxed text-[#F4F1EA]/75"
        >
          Eight luxury cottages, log fires and hot tubs gathered around a stone courtyard — surrounded by dog
          fields, proudly dog friendly, with beautiful sunset views. Roamed by our resident goats, the FERAL
          Mowers. Sleeps up to 40.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/estate"
            data-testid="hero-cta-estate"
            className="group inline-flex items-center gap-3 rounded-full bg-[#F4F1EA] px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-[#1E1610] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#C89D56]"
          >
            Explore the estate
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/enquire"
            data-testid="hero-cta-enquire"
            className="rounded-full border border-[#F4F1EA]/40 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-[#F4F1EA] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#B85D43] hover:bg-[#B85D43]"
          >
            Enquire now
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-14 grid grid-cols-2 gap-6 border-t border-[#F4F1EA]/15 pt-8 sm:grid-cols-4"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-serif text-4xl text-[#C89D56]">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4F1EA]/60">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Intro = () => (
  <section data-testid="home-intro" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-32">
    <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
      <Reveal>
        <Eyebrow>The estate</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight text-[#241A12] sm:text-4xl lg:text-5xl">
          Private cottages, shared spaces & twenty bedrooms anchored by log fires.
        </h2>
        <p className="mt-6 leading-relaxed text-[#7A6A58]">
          Surrounded by dog fields and open countryside, with uninterrupted horizons and beautiful sunset views.
          Rustic spa rituals, outdoor feasting, fire pits and a wild pond invite you to switch off — this is a
          place to gather, reset and reconnect. Dogs are very welcome.
        </p>
        <Link
          to="/estate"
          data-testid="intro-cta-estate"
          className="group mt-8 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[#B85D43] transition-colors hover:text-[#5A4632]"
        >
          See the cottages
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <Reveal delay={0.15} className="relative">
        <div className="overflow-hidden rounded-3xl">
          <img src={IMG.interior} alt="Inside a cottage at Plas Llechylched" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
        </div>
        <div className="absolute -bottom-8 -left-4 hidden w-44 rotate-[-6deg] overflow-hidden rounded-2xl border-4 border-[#FAF8F5] shadow-xl sm:block lg:-left-10">
          <img src={IMG.goat} alt="A resident goat of the FERAL Mowers" className="aspect-square w-full object-cover" />
        </div>
      </Reveal>
    </div>
  </section>
);

const Stay = () => {
  const cards = [
    { title: "The Whole Estate", tag: "Sleeps up to 40 · 20 bedrooms", img: IMG.estate, copy: "Exclusive use of the entire courtyard estate — every cottage, every shared space — for large groups, celebrations and full retreats." },
    { title: "The Courtyard Cottages", tag: "8 cottages · sleep 2–6 each", img: IMG.courtyard1, copy: "Self-contained private cottages wrapped around the courtyard. Their own front doors, log fires and slow mornings." },
    { title: "The Farmhouse", tag: "Sleeps up to 14 · shared spaces", img: IMG.farmhouse, copy: "The communal heart of the estate: big kitchen, gathering lounges, log fires and long dining tables made for feasting." },
  ];
  return (
    <section data-testid="home-stay" className="bg-[#F3EFEA] py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Stay</Eyebrow>
            <h2 className="mt-4 max-w-xl font-serif text-3xl leading-[1.15] tracking-tight text-[#241A12] sm:text-4xl lg:text-5xl">
              Eight cottages. One courtyard. Yours to make your own.
            </h2>
          </div>
          <Link
            to="/estate"
            data-testid="stay-cta-estate"
            className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[#B85D43] transition-colors hover:text-[#5A4632]"
          >
            Browse the estate
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.12}>
              <Link to="/estate" data-testid={`stay-card-${i}`} className="group block">
                <div className="overflow-hidden rounded-3xl">
                  <img src={c.img} alt={c.title} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#B85D43]">{c.tag}</p>
                <h3 className="mt-2 font-serif text-2xl text-[#241A12] transition-colors group-hover:text-[#B85D43]">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#7A6A58]">{c.copy}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Goats = () => (
  <section data-testid="goats-section" className="relative overflow-hidden bg-[#1E1610] py-20 text-[#F4F1EA] lg:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-16">
      <Reveal className="relative order-2 lg:order-1">
        <div className="absolute -inset-3 rotate-[-3deg] rounded-[2rem] border border-[#C89D56]/40" aria-hidden="true" />
        <div className="relative rotate-[2deg] overflow-hidden rounded-[2rem] shadow-2xl">
          <img src={IMG.goat} alt="One of the FERAL Mowers — resident goats at Plas Llechylched" className="aspect-[4/5] w-full object-cover" />
        </div>
        <div className="absolute -bottom-6 right-6 rotate-[-4deg] rounded-2xl bg-[#B85D43] px-5 py-3 shadow-xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4F1EA]/80">On duty since</p>
          <p className="font-serif text-2xl text-[#F4F1EA]">Dawn ’til dusk</p>
        </div>
      </Reveal>
      <Reveal delay={0.15} className="order-1 lg:order-2">
        <Eyebrow light>Resident groundskeepers</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
          Meet the <em className="text-[#C89D56]">FERAL Mowers</em>.
        </h2>
        <p className="mt-6 max-w-lg leading-relaxed text-[#F4F1EA]/70">
          Our resident goats roam the estate keeping the lawns in check — zero emissions, maximum charm. They take
          their work seriously and their ear scratches even more so. Say hello on your way to the pond; they insist.
        </p>
        <ul className="mt-8 space-y-4">
          {[
            { icon: PawPrint, text: "Free-ranging, endlessly curious, occasionally opinionated" },
            { icon: Flame, text: "Often found loitering near the fire pits at dusk" },
            { icon: Waves, text: "Strictly no swimming in the wild pond. They know the rules" },
          ].map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-4 text-sm text-[#F4F1EA]/75">
              <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#2B2015] text-[#C89D56]">
                <Icon className="h-4 w-4" />
              </span>
              {text}
            </li>
          ))}
        </ul>
        <Link
          to="/enquire"
          data-testid="goats-cta-enquire"
          className="group mt-10 inline-flex items-center gap-3 rounded-full border border-[#C89D56]/50 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-[#C89D56] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#C89D56] hover:text-[#1E1610]"
        >
          Meet them on your stay
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const WeddingsTeaser = () => (
  <section data-testid="home-weddings" className="mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-32">
    <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
      <Reveal className="relative">
        <div className="overflow-hidden rounded-3xl">
          <img src={IMG.weddingBarn} alt="A rustic barn wedding celebration" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
        </div>
        <div className="absolute -right-4 -top-6 flex rotate-[5deg] items-center gap-2 rounded-2xl bg-[#5A4632] px-5 py-3 shadow-xl lg:-right-8">
          <Star className="h-4 w-4 text-[#C89D56]" />
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#F4F1EA]">Weekend takeovers</p>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <Eyebrow>Weddings at the Plas</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight text-[#241A12] sm:text-4xl lg:text-5xl">
          Vows in the courtyard. Feasting under festoon lights.
        </h2>
        <p className="mt-6 leading-relaxed text-[#7A6A58]">
          Take over the whole estate for a wedding weekend that feels like a village of your own — ceremony on the
          lawn, long-table feasting, dancing by fire light, and forty of your favourite people sleeping over.
        </p>
        <Link
          to="/weddings"
          data-testid="weddings-cta-explore"
          className="group mt-8 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[#B85D43] transition-colors hover:text-[#5A4632]"
        >
          Plan your wedding
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const RetreatsPreview = () => (
  <section data-testid="home-retreats" className="bg-[#F3EFEA] py-20 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>Retreats</Eyebrow>
          <h2 className="mt-4 max-w-xl font-serif text-3xl leading-[1.15] tracking-tight text-[#241A12] sm:text-4xl lg:text-5xl">
            Escapes with purpose, rooted in wild beauty.
          </h2>
        </div>
        <Link
          to="/retreats"
          data-testid="retreats-cta-explore"
          className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[#B85D43] transition-colors hover:text-[#5A4632]"
        >
          All retreats
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {RETREATS.map((r, i) => (
          <Reveal key={r.title} delay={i * 0.1}>
            <Link to="/retreats" data-testid={`retreat-card-${i}`} className="group block">
              <div className="overflow-hidden rounded-2xl">
                <img src={r.img} alt={r.title} className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <h3 className="mt-4 font-serif text-xl text-[#241A12] transition-colors group-hover:text-[#B85D43]">{r.title}</h3>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CtaBanner = () => (
  <section data-testid="home-cta" className="relative overflow-hidden bg-[#5A4632] py-24 text-center text-[#F4F1EA]">
    <div className="mx-auto max-w-3xl px-4 sm:px-8">
      <Reveal>
        <Eyebrow light>Ready when you are</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
          Gather, reset and reconnect at the Plas.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-[#F4F1EA]/70">
          Tell us your dates and your dreams — a cottage for two, a wedding weekend, a full-estate retreat — and
          we'll take care of the rest.
        </p>
        <Link
          to="/enquire"
          data-testid="cta-enquire-button"
          className="mt-9 inline-block rounded-full bg-[#B85D43] px-9 py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F4F1EA] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#C89D56] hover:text-[#1E1610]"
        >
          Enquire now
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Intro />
      <Stay />
      <Goats />
      <WeddingsTeaser />
      <RetreatsPreview />
      <CtaBanner />
    </main>
  );
}
